import { useCallback } from 'react';
import { css } from '@emotion/react';
import { RouteType } from '@weave/schema-gen-ts/dist/schemas/phone/tree/v1/phone_tree_service.pb';
import { useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { theme } from '@frontend/theme';
import { Text, Heading } from '@frontend/design-system';
import { SettingsCard } from '../common/settings-card';
import { CallGroupInstructionField } from './fields/call-group';
import { CallQueueInstructionField } from './fields/call-queue';
import { ForwardDeviceInstructionFields } from './fields/forward-device';
import { ForwardNumberInstructionFields } from './fields/forward-number';
import { PhoneTreeInstructionField } from './fields/phone-tree-instruction-field';
import { PLAY_MESSAGE_BREAKPOINT_WIDTH, PlayMessageInstructionFields } from './fields/play-message';
import { ReadOnlyInstructionField } from './fields/read-only-instruction';
import { RepeatInstructionField } from './fields/repeat-instruction-field';
import { VoicemailPromptInstructionFields } from './fields/voicemail-prompt';
import { DialOptionData, DialOptionInstruction, RepeatInstruction } from './types';

export const PhoneTreeCard = ({
  options,
  onOptionChange,
  condensed,
  disableEditing = false,
}: {
  options: DialOptionData[];
  onOptionChange?: (oldOption: DialOptionData, updatedOption: DialOptionData) => void;
  condensed?: boolean;
  disableEditing?: boolean;
}) => {
  const { t } = useTranslation('phone');

  return (
    <SettingsCard
      condensed={condensed}
      title={t('Phone Tree')}
      description={t('View dial numbers and see where calls go when someone selects that number.')}
    >
      <DialOptions options={options} onOptionChange={onOptionChange} disableEditing={disableEditing} />
    </SettingsCard>
  );
};

/**
 * Lists out the dial options for the phone tree.
 */
export const DialOptions = ({
  options,
  onOptionChange,
  disableEditing = false,
  isReadOnly = false,
}: {
  options: DialOptionData[];
  onOptionChange?: (oldOption: DialOptionData, updatedOption: DialOptionData) => void;
  disableEditing?: boolean;
  isReadOnly?: boolean;
}) => {
  const { t } = useTranslation('phone');

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing(2)};
      `}
    >
      {/* Display for empty state */}
      {!options.length && (
        <section
          css={css`
            display: flex;
            justify-content: center;
            border-radius: ${theme.borderRadius.medium};
            padding: ${theme.spacing(3)};
            background-color: ${theme.colors.neutral5};
          `}
        >
          <Heading
            level={3}
            css={css`
              color: ${theme.colors.neutral50};
            `}
          >
            {t('No Dial Numbers')}
          </Heading>
        </section>
      )}

      {options.map((option) => (
        <DialOption
          disabled={disableEditing}
          option={option}
          updateOptions={(updatedOption) => onOptionChange?.(option, updatedOption)}
          key={option.number}
          isReadOnly={isReadOnly}
        />
      ))}
    </div>
  );
};

/**
 * This is the component that represents a single dial option in the phone tree. It is visually a row surrounded by a
 * gray background.
 */
const DialOption = ({
  option,
  updateOptions,
  disabled = false,
  isReadOnly = false,
}: {
  option: DialOptionData;
  updateOptions: (option: DialOptionData) => void;
  disabled?: boolean;
  isReadOnly?: boolean;
}) => {
  const { t } = useTranslation('phone');

  const onInstructionChange = useCallback(
    (instruction: DialOptionInstruction, index: number) => {
      updateOptions({
        ...option,
        routes: [...option.routes.slice(0, index), instruction, ...option.routes.slice(index + 1)],
      });
    },
    [option, updateOptions]
  );

  return (
    <section
      css={css`
        display: flex;
        width: fit-content;
        gap: ${theme.spacing(2)};
        border-radius: ${theme.borderRadius.medium};
        background-color: ${theme.colors.neutral5};
        padding: ${theme.spacing(3)};
      `}
      tabIndex={0}
      data-number={option.number}
    >
      <div
        css={css`
          margin-top: 2px;
        `}
      >
        <DialDisplay number={option.number} />
      </div>
      <div
        css={css`
          display: flex;
          flex-wrap: wrap;
          gap: ${theme.spacing(2)};
        `}
      >
        {option.routes.map((instruction, index) => (
          <Instruction
            key={`${instruction.type}-${index}`}
            instruction={instruction}
            disabled={disabled}
            isReadOnly={isReadOnly}
            onChange={(incomingInstruction) => onInstructionChange(incomingInstruction, index)}
          />
        ))}

        {/* Display for no instructions empty state */}
        {option.routes.length === 0 && (
          <div
            css={css`
              display: flex;
              gap: ${theme.spacing(2)};
              align-self: center;
            `}
          >
            <Text>{'>'}</Text>
            <Text>{t('No Steps')}</Text>
          </div>
        )}
      </div>
    </section>
  );
};

/**
 * This component handles the details of a single instruction in a dial option. It always has a type, and a corresponding payload.
 */
const Instruction = ({
  instruction,
  onChange,
  disabled = false,
  isReadOnly = false,
}: {
  instruction: DialOptionInstruction;
  onChange: (instructionPayload: DialOptionInstruction) => void;
  disabled?: boolean;
  isReadOnly?: boolean;
}) => {
  const { t } = useTranslation('phone');
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');

  const onPayloadChange = useCallback(
    (payload: DialOptionInstruction) => {
      onChange({ ...instruction, ...payload });
    },
    [instruction, onChange]
  );

  let content;
  switch (instruction.type) {
    case RouteType.ROUTE_TYPE_CALL_GROUP:
      content = (
        <CallGroupInstructionField
          isReadOnly={true}
          instructionState={instruction}
          disabled={disabled}
          onChange={onPayloadChange}
        />
      );
      break;
    case RouteType.ROUTE_TYPE_CALL_QUEUE:
      content = (
        <CallQueueInstructionField
          isReadOnly={true}
          instructionState={instruction}
          disabled={disabled}
          onChange={onPayloadChange}
        />
      );
      break;
    case RouteType.ROUTE_TYPE_CALL_ROUTE:
      content = (
        <ReadOnlyInstructionField
          instructionName={instruction.name ?? t('Unknown Call Route')}
          iconName={'call-route'}
          instructionType={t('Call Route')}
        />
      );
      break;
    case RouteType.ROUTE_TYPE_DEVICE:
      content = (
        <ForwardDeviceInstructionFields
          instructionState={instruction}
          disabled={disabled}
          onChange={onPayloadChange}
          isReadOnly={true}
          locationId={settingsTenantLocation?.locationId ?? ''}
        />
      );
      break;
    case RouteType.ROUTE_TYPE_FORWARDING_NUMBER:
      content = (
        <ForwardNumberInstructionFields
          instructionState={instruction}
          disabled={disabled}
          onChange={onPayloadChange}
          isReadOnly={isReadOnly}
          locationId={settingsTenantLocation?.locationId ?? ''}
        />
      );
      break;
    case RouteType.ROUTE_TYPE_PHONE_HOURS:
      content = (
        <ReadOnlyInstructionField
          instructionName={instruction.name ?? t('Unknown Phone Hours')}
          iconName={'clock'}
          instructionType={t('Phone Hours')}
        />
      );
      break;
    case RouteType.ROUTE_TYPE_IVR_MENU:
      content = <PhoneTreeInstructionField instructionState={instruction} disabled={disabled} />;
      break;
    case RouteType.ROUTE_TYPE_PLAY_MESSAGE:
      content = (
        <PlayMessageInstructionFields
          instructionState={instruction}
          disabled={disabled}
          isReadOnly={isReadOnly}
          onChange={onPayloadChange}
        />
      );
      break;
    case RouteType.ROUTE_TYPE_REPEAT:
      content = <RepeatInstructionField instructionState={instruction as RepeatInstruction} />;
      break;
    case RouteType.ROUTE_TYPE_VOICEMAIL:
      content = (
        <VoicemailPromptInstructionFields
          instructionState={instruction}
          isReadOnly={isReadOnly}
          disabled={disabled}
          onChange={onPayloadChange}
        />
      );
      break;
    case RouteType.ROUTE_TYPE_WEAVE_ASSISTANT:
      content = <ReadOnlyInstructionField instructionName={t('Weave Assistant')} iconName='aiassistant' />;
      break;
    default:
      content = null;
  }

  if (!content || instruction.shouldHide) {
    return null;
  }

  return (
    <div
      css={css`
        display: flex;
        gap: ${theme.spacing(2)};
        align-items: ${
          // Special case: For voicemail instructions, use baseline alignment to align better with the greeting
          // dropdown and audio scrubber which are all part of the voicemail instruction column.
          // For all other instruction types, center alignment looks better.
          instruction.type === RouteType.ROUTE_TYPE_VOICEMAIL ? 'baseline' : 'center'
        };
      `}
    >
      <Text
        css={[
          css`
            @media (max-width: 200px) {
              align-self: baseline;
            }
          `,
          // Special case: For play message instructions, use flex-start alignment to align better with the greeting
          // dropdown and audio scrubber which are all part of the play message instruction column.
          instruction.type === RouteType.ROUTE_TYPE_PLAY_MESSAGE &&
            css`
              @media (max-width: ${PLAY_MESSAGE_BREAKPOINT_WIDTH}px) {
                align-self: flex-start;
                position: relative;
                top: 8px;
              }
            `,
        ]}
      >
        {'>'}
      </Text>
      {content}
    </div>
  );
};

/**
 * A component that displays the text "Dial" followed by
 * a blue circle with a number in the center.
 */
const DialDisplay = ({ number }: { number: number }) => {
  const { t } = useTranslation('phone');
  return (
    <div
      css={css`
        display: flex;
        align-items: center; /* Vertically aligns the text and circle */
        gap: ${theme.spacing(1)};
      `}
    >
      <Text>{t('Dial')}</Text>
      <Text
        css={css`
          display: flex;
          justify-content: center; /* Centers the number horizontally */
          align-items: center; /* Centers the number vertically */
          background-color: ${theme.colors.primary60};
          color: ${theme.colors.white};
          width: 32px; /* Circle width */
          height: 32px; /* Circle height (same as width) */
          border-radius: 50%; /* Makes the square a circle */
          font-size: 16px;
        `}
      >
        {number}
      </Text>
    </div>
  );
};
